import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, X, FileText, Star } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

function AssignEditor({ chart, products, categories, onClose, onSaved }) {
  const [name, setName] = useState(chart.name);
  const [pids, setPids] = useState(chart.product_ids || []);
  const [cslugs, setCslugs] = useState(chart.category_slugs || []);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/admin/size-charts/${chart.id}`, { name, product_ids: pids, category_slugs: cslugs });
      toast.success("Size chart updated");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const shown = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 12);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="size-chart-assign-modal">
      <div className="bg-[#FAF8F5] max-w-xl mx-auto lg:my-10 min-h-screen lg:min-h-0 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">Assign — {chart.name}</h2>
          <button onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <label className="tq-label">Chart Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="chart-rename" className="tq-input mb-4" />
        <label className="tq-label">Assign to Products ({pids.length} selected)</label>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="tq-input !py-2 mb-2" />
        <div className="max-h-48 overflow-y-auto border border-[#E5DEC9] bg-white p-2 mb-4">
          {shown.map((p) => (
            <label key={p.id} className="flex items-center gap-2 py-1.5 px-1 text-sm cursor-pointer hover:bg-[#FAF8F5]">
              <input type="checkbox" checked={pids.includes(p.id)} onChange={() => toggle(pids, setPids, p.id)} data-testid={`chart-product-${p.slug}`} className="accent-[#D4AF37] w-4 h-4" />
              {p.name}
            </label>
          ))}
        </div>
        <label className="tq-label">Assign to Categories ({cslugs.length} selected)</label>
        <div className="flex flex-wrap gap-2 mb-5">
          {categories.map((c) => (
            <button key={c.slug} type="button" onClick={() => toggle(cslugs, setCslugs, c.slug)} data-testid={`chart-category-${c.slug}`}
              className={`px-3 py-1.5 text-xs border ${cslugs.includes(c.slug) ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{c.name}</button>
          ))}
        </div>
        <button onClick={save} disabled={busy} data-testid="chart-assign-save" className="tq-btn-dark w-full">{busy ? "Saving…" : "Save Assignment"}</button>
      </div>
    </div>
  );
}

export default function AdminSizeCharts() {
  const [charts, setCharts] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/size-charts").then(({ data }) => setCharts(data));
  useEffect(() => {
    load();
    api.get("/admin/products", { params: { limit: 200 } }).then(({ data }) => setProducts(data.products));
    api.get("/admin/categories").then(({ data }) => setCategories(data));
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.pdf$/i, ""));
      await api.post("/admin/size-charts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Size chart PDF uploaded");
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const setDefault = async (id) => { await api.put(`/admin/size-charts/${id}`, { is_default: true }); toast.success("Default size chart set"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this size chart PDF?")) return; await api.delete(`/admin/size-charts/${id}`); load(); };

  return (
    <div data-testid="admin-size-charts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl">Size Charts</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Upload size-chart PDFs and assign them to products or categories. The editable table chart (Store Settings → Size Chart) stays available and is used when no PDF is assigned.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="chart-upload-btn" className="tq-btn-dark !py-2.5">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload PDF"}
        </button>
        <input ref={fileRef} type="file" accept="application/pdf" onChange={upload} className="hidden" data-testid="chart-upload-input" />
      </div>
      {!charts ? <div className="h-40 bg-white animate-pulse" /> : charts.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-12 text-center text-[#6E6D6A]">No size chart PDFs yet. The editable HTML table chart remains active on all products.</div>
      ) : (
        <div className="space-y-3">
          {charts.map((c) => (
            <div key={c.id} className="bg-white border border-[#E5DEC9] p-4 flex flex-wrap items-center gap-4" data-testid={`chart-row-${c.id}`}>
              <FileText size={28} className="text-[#8B1E21] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium">{c.name} {c.is_default && <span className="ml-2 text-[9px] font-bold uppercase bg-[#D4AF37] px-1.5 py-0.5">Default</span>}</p>
                <p className="text-xs text-[#9a938a]">{(c.product_ids || []).length} product(s) · {(c.category_slugs || []).length} categor(ies) · by {c.by}</p>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${c.url}`} target="_blank" rel="noopener noreferrer" data-testid={`chart-view-${c.id}`} className="text-xs text-[#C5A059] underline">View PDF</a>
              {!c.is_default && <button onClick={() => setDefault(c.id)} data-testid={`chart-default-${c.id}`} className="tq-btn-outline !py-1.5 !px-3 !text-[10px]"><Star size={12} /> Set Default</button>}
              <button onClick={() => setEditing(c)} data-testid={`chart-assign-${c.id}`} className="tq-btn-dark !py-1.5 !px-3 !text-[10px]">Assign / Rename</button>
              <button onClick={() => del(c.id)} aria-label="Delete chart" className="p-1.5 text-[#8B1E21]"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      {editing && <AssignEditor chart={editing} products={products} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
